/**
 * Poses the anatomical tongue (BodyParts3D, public/articulator/tongue.json, built by
 * ml/src/foe_ml/articulator/tongue.py) by its midline dorsum.
 *
 * Binding: every vertex is tied to the nearest point of the rest dorsum (TONGUE_DORSUM_REST,
 * anatomy.gen.ts) by its arc position s (0 at the tip, 1 at the root), its offset along the
 * dorsum (a) and across it (b, negative below the surface), and its distance from the midline (x).
 *
 * Posing: given a target dorsum (the same number of points, tip to root), each vertex goes to the
 * same (a, b) in the target's local frame at s, so the tongue's real cross-sections and bulk move
 * with its surface. Deep tissue (near the floor of the mouth and the hyoid) follows less, fading
 * out over DEPTH_FALLOFF mm below the surface. Lateral shaping (bracing, grooving, lowered sides)
 * is a height change that grows toward the sides.
 */
import type { ProfilePoint } from './articulation';

/** Below this depth (mm) a vertex stays where it is at rest. */
export const DEPTH_FALLOFF = 30;
/** Off the dorsum's ends, points follow fully within ALONG_FREE mm and not beyond that plus ALONG_FALLOFF. */
const ALONG_FREE = 2;
/** The free front of the tongue (tip and blade, a share of the dorsum from the tip) moves as a whole. */
const FREE_FRONT = 0.15;
/** The share of the dorsum from the tip over which front thickening fades out. */
const FRONT_REACH = 0.35;
const ALONG_FALLOFF = 8;

export interface TongueBinding {
  rest: Float32Array; // [x, y, z] per vertex, mm
  s: Float32Array;
  a: Float32Array;
  b: Float32Array;
  /** The tongue's half width at rest at each vertex's s (for lateral shaping). */
  halfWidth: Float32Array;
  /** The tongue's thickness below the dorsum at rest at each vertex's s (mm). */
  thickness: Float32Array;
  /** Rest volume (mm^3), computed on first use. */
  restVolume?: number;
  /** Mesh neighbours (CSR: offsets into `neighbours`), built on first use with faces. */
  neighbourStart?: Uint32Array;
  neighbours?: Uint32Array;
}

interface Frame {
  z: number;
  y: number;
  tz: number;
  ty: number;
}

/** Arc-length samples of a polyline with unit tangents; `count` points from the first to the last. */
export function curveFrames(curve: readonly ProfilePoint[], count: number): Frame[] {
  const lengths = [0];
  for (let i = 1; i < curve.length; i++) {
    const [z0, y0] = curve[i - 1] ?? [0, 0];
    const [z1, y1] = curve[i] ?? [0, 0];
    lengths.push((lengths[i - 1] ?? 0) + Math.hypot(z1 - z0, y1 - y0));
  }
  const total = lengths[lengths.length - 1] ?? 1;
  const out: Frame[] = [];
  let j = 1;
  for (let k = 0; k < count; k++) {
    const target = (k / (count - 1)) * total;
    while (j < curve.length - 1 && (lengths[j] ?? 0) < target) j++;
    const [z0, y0] = curve[j - 1] ?? [0, 0];
    const [z1, y1] = curve[j] ?? [0, 0];
    const l0 = lengths[j - 1] ?? 0;
    const seg = Math.max(1e-9, (lengths[j] ?? 0) - l0);
    const f = Math.min(1, Math.max(0, (target - l0) / seg));
    out.push({
      z: z0 + (z1 - z0) * f,
      y: y0 + (y1 - y0) * f,
      tz: (z1 - z0) / seg,
      ty: (y1 - y0) / seg,
    });
  }
  // Smooth the tangents over about 3 % of the curve on each side (polyline corners), so the
  // frames, and the tissue carried by them, turn gradually.
  const reach = Math.max(1, Math.round(count * 0.03));
  return out.map((p, i) => {
    let tz = 0;
    let ty = 0;
    for (let d = -reach; d <= reach; d++) {
      const q = out[Math.min(out.length - 1, Math.max(0, i + d))] ?? p;
      tz += q.tz;
      ty += q.ty;
    }
    const len = Math.hypot(tz, ty) || 1;
    return { ...p, tz: tz / len, ty: ty / len };
  });
}

const NO_FRAME: Frame = { z: 0, y: 0, tz: -1, ty: 0 };
const frameAt = (frames: readonly Frame[], i: number): Frame => frames[i] ?? NO_FRAME;

/** The outward normal of the dorsum (up and forward over the tongue), from its tangent. */
const normal = (f: Frame): [number, number] => [f.ty, -f.tz];

const SAMPLES = 240;

export function bindTongue(vertices: ArrayLike<number>, restDorsum: readonly ProfilePoint[]) {
  const frames = curveFrames(restDorsum, SAMPLES);
  const n = vertices.length / 3;
  const binding: TongueBinding = {
    rest: Float32Array.from(vertices),
    s: new Float32Array(n),
    a: new Float32Array(n),
    b: new Float32Array(n),
    halfWidth: new Float32Array(n),
    thickness: new Float32Array(n),
  };
  // Half width per sample: the widest vertex near each dorsum position.
  const widths = new Float32Array(SAMPLES);
  const depths = new Float32Array(SAMPLES);
  for (let v = 0; v < n; v++) {
    const x = vertices[v * 3] ?? 0;
    const y = vertices[v * 3 + 1] ?? 0;
    const z = vertices[v * 3 + 2] ?? 0;
    let best = Infinity;
    let k = 0;
    for (let i = 0; i < SAMPLES; i++) {
      const f = frameAt(frames, i);
      const d = (z - f.z) ** 2 + (y - f.y) ** 2;
      if (d < best) {
        best = d;
        k = i;
      }
    }
    const f = frameAt(frames, k);
    const [nz, ny] = normal(f);
    binding.s[v] = k / (SAMPLES - 1);
    binding.a[v] = (z - f.z) * f.tz + (y - f.y) * f.ty;
    binding.b[v] = (z - f.z) * nz + (y - f.y) * ny;
    widths[k] = Math.max(widths[k] ?? 0, Math.abs(x));
    depths[k] = Math.max(depths[k] ?? 0, -(binding.b[v] ?? 0));
  }
  // Fill gaps and smooth the width profile.
  let last = 0;
  for (let i = 0; i < SAMPLES; i++) last = widths[i] = Math.max(widths[i] ?? 0, last * 0.9);
  last = 0;
  for (let i = 0; i < SAMPLES; i++) last = depths[i] = Math.max(depths[i] ?? 0, last * 0.9);
  for (let v = 0; v < n; v++) {
    const k = Math.round((binding.s[v] ?? 0) * (SAMPLES - 1));
    binding.halfWidth[v] = Math.max(4, widths[k] ?? 4);
    binding.thickness[v] = Math.max(6, depths[k] ?? 6);
  }
  return binding;
}

export interface TonguePose {
  /** Target dorsum, tip to root, mm [z, y]. */
  dorsum: readonly ProfilePoint[];
  /**
   * Height change at the sides (mm, at the full half width) along s (evenly spaced, tip to
   * root): positive raises the sides (bracing against the molars, a midline groove), negative
   * lowers them (the lateral channels of /l/).
   */
  sides?: readonly number[];
  /**
   * How much thicker the front of the tongue gets (1 = as at rest), fading out by FRONT_REACH of
   * the dorsum from the tip: a tongue that bunches up shortens and thickens at the front, like any
   * muscle that contracts.
   */
  frontThickness?: number;
  /**
   * The floor of the mouth along the midline (mm, [z, y], front to back): the underside settles
   * onto it (the tongue is attached there, not floating). Absent: the underside keeps its shape.
   */
  floor?: readonly ProfilePoint[];
  /**
   * Lateral bracing (/r/): how far the tongue's side margins go to the palate's edge at the upper
   * molars (0 not at all, 1 touching), along s (evenly spaced, tip to root), and the palate's
   * cross-section at a depth (half width and the height of its edge at the gums, mm). The midline
   * stays where the dorsum puts it, so a groove forms between the braced sides.
   */
  brace?: {
    weights: readonly number[];
    palate: (z: number) => { halfWidth: number; edgeY: number } | undefined;
  };
}

/** The margin is placed this far inside the palate's edge (mm): resting against the teeth. */
const BRACE_INSET = 1.2;
/** The top-lateral corner is looked for this far below the top of each slice (mm). */
const CORNER_DEPTH = 5;
/** Smoothing passes for the bracing (light: it is local to the margins). */
const BRACE_SMOOTHING_PASSES = 6;
/**
 * How far down the sides the bracing lift reaches (mm). With 16 mm and 2 smoothing passes the
 * raised sides met the top at a tight corner and the r looked like a box (owner, 2026-10-14).
 */
const BRACE_FALLOFF_MM = 22;

const lerpAt = (values: readonly number[], s: number) => {
  if (!values.length) return 0;
  const x = Math.min(1, Math.max(0, s)) * (values.length - 1);
  const i = Math.min(values.length - 2, Math.floor(x));
  if (i < 0) return values[0] ?? 0;
  const f = x - i;
  return (values[i] ?? 0) * (1 - f) + (values[i + 1] ?? 0) * f;
};

/**
 * Writes the posed vertices (mm, [x, y, z] per vertex) into `out`. With `faces`, the width is
 * rescaled so the volume stays that of the rest tongue (muscle is incompressible: a tongue that
 * rises narrows, one that flattens spreads).
 */
export function poseTongue(
  binding: TongueBinding,
  pose: TonguePose,
  out: Float32Array,
  faces?: ArrayLike<number>,
) {
  const frames = curveFrames(pose.dorsum, SAMPLES);
  const n = binding.s.length;
  for (let v = 0; v < n; v++) {
    const x = binding.rest[v * 3] ?? 0;
    const ry = binding.rest[v * 3 + 1] ?? 0;
    const rz = binding.rest[v * 3 + 2] ?? 0;
    const s = binding.s[v] ?? 0;
    const a = binding.a[v] ?? 0;
    const thicken = 1 + ((pose.frontThickness ?? 1) - 1) * Math.max(0, 1 - s / FRONT_REACH);
    const b = Math.min(0, binding.b[v] ?? 0) * thicken + Math.max(0, binding.b[v] ?? 0);
    const f = frameAt(frames, Math.round(s * (SAMPLES - 1)));
    const [nz, ny] = normal(f);
    let z = f.z + a * f.tz + b * nz;
    let y = f.y + a * f.ty + b * ny;
    // The underside rests on the floor of the mouth: through the tongue's thickness a point
    // follows the dorsum less the deeper it lies (fully at the surface, not at all at the floor),
    // except the free front (tip and blade), which moves as a whole. Deep tissue beyond
    // DEPTH_FALLOFF never follows. Points off the dorsum's ends (the underside and floor bind to
    // the nearest dorsum point at a distance along it) fade too.
    const relative = Math.min(1, Math.max(0, 1 + Math.min(0, b) / (binding.thickness[v] ?? 6)));
    const free = Math.min(1, Math.max(0, 1 - s / FREE_FRONT));
    const depth = Math.min(
      Math.max(relative, free * free * (3 - 2 * free)),
      Math.max(0, 1 + Math.min(0, b) / DEPTH_FALLOFF),
    );
    const along = Math.min(1, Math.max(0, 1 - (Math.abs(a) - ALONG_FREE) / ALONG_FALLOFF));
    const w = depth * depth * (3 - 2 * depth) * along * along * (3 - 2 * along);
    z = rz + (z - rz) * w;
    y = ry + (y - ry) * w;
    // Sides: a height change growing with (x / half width)^2, on the upper half of the tongue.
    const u = Math.min(1, Math.abs(x) / (binding.halfWidth[v] ?? 1));
    const upper = Math.min(1, Math.max(0, 1 + b / 15));
    y += lerpAt(pose.sides ?? [], s) * u * u * upper;
    // The underside (the deepest third of the tongue's thickness) settles onto the floor.
    if (pose.floor) {
      const floorY = floorAt(pose.floor, z);
      const rel = -Math.min(0, binding.b[v] ?? 0) / (binding.thickness[v] ?? 6);
      const k = Math.min(1, Math.max(0, (rel - 0.6) / 0.4));
      if (floorY !== undefined && s > FREE_FRONT) y += (floorY - y) * k * k * (3 - 2 * k);
    }
    out[v * 3] = x;
    out[v * 3 + 1] = y;
    out[v * 3 + 2] = z;
  }
  if (faces) {
    smoothDisplacement(binding, faces, out);
    binding.restVolume ??= meshVolume(binding.rest, faces);
    // Volume is linear in the width scale (x only), so one rescale restores it.
    const k = binding.restVolume / Math.max(1, meshVolume(out, faces));
    for (let v = 0; v < n; v++) out[v * 3] = (out[v * 3] ?? 0) * k;
  }
  if (pose.brace) brace(binding, pose.brace, out, faces);
  return out;
}

/**
 * Moves the upper side margins out and up to the palate's edge (lateral bracing). Per depth, the
 * tongue's widest point in its upper part goes to the palate's half width less BRACE_INSET; points
 * follow by how far out they are (none in the middle third) and how high (the upper part), so the
 * sides rise against the teeth and the middle stays lower: a groove. Smoothed after.
 */
function brace(
  binding: TongueBinding,
  setting: NonNullable<TonguePose['brace']>,
  out: Float32Array,
  faces?: ArrayLike<number>,
) {
  const n = binding.s.length;
  const before = Float32Array.from(out);
  // The posed tongue per 1 mm depth slice: its half width, its top, and the top of its side
  // margins (the outer fifth of the width).
  const width = new Map<number, number>();
  const top = new Map<number, number>();
  for (let v = 0; v < n; v++) {
    const key = Math.round(out[v * 3 + 2] ?? 0);
    width.set(key, Math.max(width.get(key) ?? 0, Math.abs(out[v * 3] ?? 0)));
    top.set(key, Math.max(top.get(key) ?? -Infinity, out[v * 3 + 1] ?? 0));
  }
  // The top-lateral corner per slice: the widest point within CORNER_DEPTH of the top, and its
  // height. Bracing moves it to the palate's edge (inside the molars, at the gums).
  const corner = new Map<number, { x: number; y: number }>();
  for (let v = 0; v < n; v++) {
    const key = Math.round(out[v * 3 + 2] ?? 0);
    const y = out[v * 3 + 1] ?? 0;
    if (y < (top.get(key) ?? 0) - CORNER_DEPTH) continue;
    const x = Math.abs(out[v * 3] ?? 0);
    const c = corner.get(key);
    if (!c || x > c.x) corner.set(key, { x, y });
  }
  for (let v = 0; v < n; v++) {
    const weight = lerpAt(setting.weights, binding.s[v] ?? 0);
    if (weight <= 0) continue;
    const x = out[v * 3] ?? 0;
    const y = out[v * 3 + 1] ?? 0;
    const z = out[v * 3 + 2] ?? 0;
    const palate = setting.palate(z);
    const key = Math.round(z);
    const c = corner.get(key);
    const crest = top.get(key) ?? y;
    if (!palate || !c || c.x <= 0) continue;
    // Out from the middle third to the corner; from the top down through the upper part.
    const u = Math.min(1, Math.max(0, (Math.abs(x) / c.x - 0.33) / 0.67));
    const height = Math.min(1, Math.max(0, 1 - (crest - y) / BRACE_FALLOFF_MM));
    const k = weight * u * u * (3 - 2 * u) * height;
    const dx = Math.max(0, palate.halfWidth - BRACE_INSET - c.x);
    const dy = Math.max(0, palate.edgeY - BRACE_INSET - c.y);
    out[v * 3] = x + Math.sign(x) * dx * k;
    out[v * 3 + 1] = y + dy * k;
  }
  if (faces) {
    // Smooth only the bracing's own displacement, so it blends into the rest of the tongue.
    const saved = binding.rest;
    binding.rest = before;
    smoothDisplacement(binding, faces, out, BRACE_SMOOTHING_PASSES);
    binding.rest = saved;
  }
}

/** The floor's height at depth z, by linear interpolation (front to back); undefined outside. */
function floorAt(floor: readonly ProfilePoint[], z: number): number | undefined {
  for (let i = 0; i + 1 < floor.length; i++) {
    const [z0, y0] = floor[i] ?? [0, 0];
    const [z1, y1] = floor[i + 1] ?? [0, 0];
    if (z <= z0 && z >= z1 && z0 !== z1) return y0 + ((y1 - y0) * (z - z0)) / (z1 - z0);
  }
  return undefined;
}

/** Passes of displacement smoothing (removes shear wrinkles, keeps the overall shape). */
const SMOOTHING_PASSES = 8;

function neighbourLists(binding: TongueBinding, faces: ArrayLike<number>) {
  if (binding.neighbourStart && binding.neighbours) {
    return { start: binding.neighbourStart, list: binding.neighbours };
  }
  const n = binding.s.length;
  const sets: Set<number>[] = Array.from({ length: n }, () => new Set<number>());
  for (let i = 0; i < faces.length; i += 3) {
    const a = faces[i] ?? 0;
    const b = faces[i + 1] ?? 0;
    const c = faces[i + 2] ?? 0;
    sets[a]?.add(b).add(c);
    sets[b]?.add(a).add(c);
    sets[c]?.add(a).add(b);
  }
  const start = new Uint32Array(n + 1);
  for (let v = 0; v < n; v++) start[v + 1] = (start[v] ?? 0) + (sets[v]?.size ?? 0);
  const list = new Uint32Array(start[n] ?? 0);
  for (let v = 0; v < n; v++) list.set([...(sets[v] ?? [])], start[v] ?? 0);
  binding.neighbourStart = start;
  binding.neighbours = list;
  return { start, list };
}

/** Averages each vertex's displacement from rest with its neighbours', a few passes. */
function smoothDisplacement(
  binding: TongueBinding,
  faces: ArrayLike<number>,
  out: Float32Array,
  passes = SMOOTHING_PASSES,
) {
  const { start, list } = neighbourLists(binding, faces);
  const n = binding.s.length;
  let d = new Float32Array(n * 3);
  for (let i = 0; i < n * 3; i++) d[i] = (out[i] ?? 0) - (binding.rest[i] ?? 0);
  let next = new Float32Array(n * 3);
  for (let pass = 0; pass < passes; pass++) {
    for (let v = 0; v < n; v++) {
      const s0 = start[v] ?? 0;
      const s1 = start[v + 1] ?? 0;
      let x = 0;
      let y = 0;
      let z = 0;
      for (let k = s0; k < s1; k++) {
        const u = (list[k] ?? 0) * 3;
        x += d[u] ?? 0;
        y += d[u + 1] ?? 0;
        z += d[u + 2] ?? 0;
      }
      const m = Math.max(1, s1 - s0);
      next[v * 3] = 0.5 * (d[v * 3] ?? 0) + (0.5 * x) / m;
      next[v * 3 + 1] = 0.5 * (d[v * 3 + 1] ?? 0) + (0.5 * y) / m;
      next[v * 3 + 2] = 0.5 * (d[v * 3 + 2] ?? 0) + (0.5 * z) / m;
    }
    [d, next] = [next, d];
  }
  for (let i = 0; i < n * 3; i++) out[i] = (binding.rest[i] ?? 0) + (d[i] ?? 0);
}

/** The enclosed volume (mm^3) of a closed triangle mesh. */
export function meshVolume(vertices: ArrayLike<number>, faces: ArrayLike<number>): number {
  let sum = 0;
  for (let i = 0; i < faces.length; i += 3) {
    const a = (faces[i] ?? 0) * 3;
    const b = (faces[i + 1] ?? 0) * 3;
    const c = (faces[i + 2] ?? 0) * 3;
    const [ax, ay, az] = [vertices[a] ?? 0, vertices[a + 1] ?? 0, vertices[a + 2] ?? 0];
    const [bx, by, bz] = [vertices[b] ?? 0, vertices[b + 1] ?? 0, vertices[b + 2] ?? 0];
    const [cx, cy, cz] = [vertices[c] ?? 0, vertices[c + 1] ?? 0, vertices[c + 2] ?? 0];
    sum += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
  }
  return Math.abs(sum) / 6;
}
